import React from 'react';
import { ChangeProposal, EntityType } from '../../types/workflow';

interface DiffViewProps {
  proposal: ChangeProposal;
}

interface DiffItem {
  field: string;
  label: string;
  oldValue: any;
  newValue: any;
  type: 'text' | 'number' | 'date' | 'boolean' | 'array' | 'object';
}

export const DiffView: React.FC<DiffViewProps> = ({ proposal }) => {
  const getDiffItems = (): DiffItem[] => {
    const items: DiffItem[] = [];
    const originalData = proposal.originalData || {};
    const proposedChanges = proposal.proposedChanges || {};

    // Get field labels based on entity type
    const getFieldLabel = (field: string): string => {
      const labelMaps = {
        [EntityType.STAKEHOLDER]: {
          firstName: 'First Name',
          lastName: 'Last Name',
          email: 'Email',
          phone: 'Phone',
          location: 'Location',
          bio: 'Biography',
          startDate: 'Start Date',
          endDate: 'End Date',
        },
        [EntityType.ORGANIZATION]: {
          name: 'Name',
          type: 'Type',
          description: 'Description',
          email: 'Email',
          phone: 'Phone',
          address: 'Address',
          website: 'Website',
          parentId: 'Parent Organization',
        },
        [EntityType.ASSIGNMENT]: {
          role: 'Role',
          department: 'Department',
          startDate: 'Start Date',
          endDate: 'End Date',
          isCurrent: 'Current Position',
        },
        [EntityType.WIKI_PAGE]: {
          title: 'Title',
          content: 'Content',
          tags: 'Tags',
        },
      };

      const entityLabels = labelMaps[proposal.entityType] || {};
      return (entityLabels as any)[field] || field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1').trim();
    };

    const getFieldType = (value: any): DiffItem['type'] => {
      if (typeof value === 'boolean') return 'boolean';
      if (typeof value === 'number') return 'number';
      if (Array.isArray(value)) return 'array';
      if (value && typeof value === 'object') return 'object';
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return 'date';
      return 'text';
    };

    // Compare all fields in proposed changes
    Object.keys(proposedChanges).forEach(field => {
      const oldValue = originalData[field];
      const newValue = proposedChanges[field];

      // Only show if values are different
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        items.push({
          field,
          label: getFieldLabel(field),
          oldValue,
          newValue,
          type: getFieldType(newValue),
        });
      }
    });

    return items;
  };

  const formatValue = (value: any, type: DiffItem['type']): string => {
    if (value === null || value === undefined) {
      return '(empty)';
    }

    switch (type) {
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'array':
        return Array.isArray(value) ? value.join(', ') : String(value);
      case 'object':
        return JSON.stringify(value, null, 2);
      default:
        return String(value);
    }
  };

  const diffItems = getDiffItems();

  if (diffItems.length === 0) {
    return (
      <p className="rounded-2xl bg-neutral-50 px-4 py-6 text-center text-[13px] text-neutral-500">No changes detected</p>
    );
  }

  const isEmpty = (value: any) => value === null || value === undefined;

  const valueLine = (item: DiffItem, side: 'old' | 'new') => {
    const value = side === 'old' ? item.oldValue : item.newValue;
    const text = formatValue(value, item.type);
    const removed = side === 'old';
    const tint = removed ? 'bg-primary-50 text-primary-700' : 'bg-emerald-50 text-emerald-800';
    const empty = isEmpty(value);

    return (
      <div className={`flex min-w-0 gap-2 rounded-lg px-2.5 py-1.5 ${tint}`}>
        <span
          className={`select-none font-ui-mono text-[12px] leading-5 ${removed ? 'text-primary-400' : 'text-emerald-500'}`}
          aria-hidden="true"
        >
          {removed ? '−' : '+'}
        </span>
        <span className="sr-only">{removed ? 'Current:' : 'Proposed:'}</span>
        {item.type === 'object' && !empty ? (
          <pre className={`min-w-0 flex-1 whitespace-pre-wrap break-words font-ui-mono text-[12px] leading-5 ${removed ? 'line-through decoration-primary-300' : ''}`}>
            {text}
          </pre>
        ) : (
          <span
            className={`min-w-0 flex-1 whitespace-pre-wrap break-words text-[13px] leading-5 ${
              empty ? 'italic opacity-60' : removed ? 'line-through decoration-primary-300' : ''
            }`}
          >
            {text}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-neutral-200">
      <ul className="divide-y divide-neutral-200/70" aria-label={`${diffItems.length} change${diffItems.length !== 1 ? 's' : ''}`}>
        {diffItems.map((item) => (
          <li key={item.field} className="px-4 py-3">
            <p className="mb-1.5 text-[12px] text-neutral-500">{item.label}</p>
            <div className="space-y-1">
              {valueLine(item, 'old')}
              {valueLine(item, 'new')}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
